import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import * as GiftAidActions from 'libs/gb-front/item/data-access-gift-aid/src/lib/+state/gift-aid.actions'
import * as GoogleAnalyticsActions from 'libs/shared/google-analytics/src/lib/+state/google-analytics.actions'
import * as ItemActions from 'libs/gb-front/item/data-access/src/lib/+state/items.actions'
import * as PaymentActions from 'libs/gb-front/payment/data-access/src/lib/+state/payment.actions'
import * as ProfileActions from 'libs/gb-front/profile/data-access/src/lib/+state/profiles.actions'
import * as ProfileItemActivitiesActions from 'libs/gb-front/item/data-access-profile-item-activities/src/lib/+state/profile-item-activities.actions'
import * as SelectedItemActions from './selected-item.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import * as StripeActions from 'libs/gb-front/payment/data-access-stripe/src/lib/+state/gb-stripe.actions'

import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { switchMap, catchError, withLatestFrom, map, mergeMap, exhaustMap, filter, of, zip, tap } from 'rxjs'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { SelectedItemApiService } from './selected-item-api.service'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { GbPaymentDialogComponent } from 'libs/gb-front/payment/util/src/lib/gb-payment-dialog/gb-payment-dialog.component'
import { SelectedItemFacade } from './selected-item.facade'
import { GbPaymentStepperViewController } from 'libs/gb-front/payment/util/src/lib/gb-payment-stepper/gb-payment-stepper-view-controller.service'
import { GbStripeService } from 'libs/gb-front/payment/data-access-stripe/src/lib/gb-stripe.service'
import {
  GbPaymentDialogConfig,
  PaymentTypeEnum,
  PreAuthResultEnum,
} from 'libs/gb-front/payment/util/src/lib/gb-payment.models'
import { Action } from '@ngrx/store'
import { ItemTypeEnum, TicketPackageEntity } from 'libs/gb-front/item/data-access/src/lib/+state/items.models'
import { ProfileTypeEnum } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.models'
import { ItemEntity } from './selected-item.models'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { GbBiddingItemHelperService } from 'libs/gb-front/item/util/src/lib/gb-bidding-item-helper/gb-bidding-item-helper.service'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import { GbSelectGuestConfirmationDialogComponent } from 'libs/gb-front/volunteer/feature/src/lib/gb-select-guest-confirmation-dialog/gb-select-guest-confirmation-dialog.component'
import { GbRaffleItemHelperService } from 'libs/gb-front/item/util/src/lib/gb-raffle-item-helper/gb-raffle-item-helper.service'
import { ItemFacade } from 'libs/gb-front/item/data-access/src/lib/+state/items.facade'
import { GoogleAnalyticsItem } from 'libs/shared/google-analytics/src/lib/+state/google-analytics.models'
import { SelectedItemState } from './selected-item.reducer'
import { GbPreAuthDialogComponent } from 'libs/gb-front/payment/util/src/lib/gb-pre-auth-dialog/gb-pre-auth-dialog.component'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { PaymentProcessEnum } from 'libs/gb-front/auction/data-access/src/lib/+state/payment-methods.models'
import { GbItemTermsAndConditionsDialogComponent } from '../../../../../item/feature/src/lib/gb-item-terms-and-conditions-dialog/gb-item-terms-and-conditions-dialog.component'

@Injectable()
export class SelectedItemEffects {
  initSelectedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.initSelectedItem),
      concatLatestFrom((action) => this.itemsFacade.selectItemById$(action.uuid, true)),
      switchMap(([action, selectedItemFromList]) => {
        const actions: Action[] = []
        if (selectedItemFromList) {
          actions.push(
            SelectedItemActions.initSelectedItemFieldsFromList({
              selectedItem: selectedItemFromList,
            }),
          )
        }
        actions.push(SelectedItemActions.syncSelectedItem({ uuid: action.uuid }))
        return actions
      }),
    ),
  )

  syncSelectedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.syncSelectedItem),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.selectedItemApiService.getItem(urlName, action.uuid).pipe(
          map((selectedItem) => SelectedItemActions.syncSelectedItemSuccess({ selectedItem })),
          catchError(async (error) => SelectedItemActions.syncSelectedItemFailure({ error, uuid: action.uuid })),
        ),
      ),
    ),
  )

  updateSelectedItemInList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.syncSelectedItemSuccess),
      map((action) => ItemActions.syncItemSuccess({ item: action.selectedItem })),
    ),
  )

  syncSelectedItemFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.syncSelectedItemFailure),
      filter((action) => action.error.status === 404),
      switchMap((action) => [
        ItemActions.deleteItemIfExistsInState({ uuid: action.uuid }),
        ItemActions.redirectItemNotFound(),
      ]),
    ),
  )

  trackViewItemEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.initSelectedItem),
      gbCheckLoaded(this.selectedItemFacade.loaded$),
      withLatestFrom(this.selectedItemFacade.selectedItem$),
      map(([, selectedItem]) =>
        GoogleAnalyticsActions.trackViewItemEvent({
          item: this.createGoogleAnalyticsItem(selectedItem),
        }),
      ),
    ),
  )

  /**
   * Used for place Bid, Donation, Raffle Purchase or Purchase Item
   * @param amount represents different thing based on item type:
   * Donation - how much the guest is donating
   * Raffle Purchase - how many tickets
   * Purchase (Buy-it-now) - how many units guest is buying
   * Bids - how much the guest is bidding on the item
   */
  placeItemActivity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SelectedItemActions.placeItemActivity),
      withLatestFrom(
        this.authFacade.authProfile$,
        this.selectedProfileFacade.selectedProfile$,
        this.selectedItemFacade.selectedItem$,
      ),
      switchMap(([, authProfile, selectedProfile, item]) => {
        const actions: Action[] = []

        if (!authProfile || authProfile?.uuid === '') {
          actions.push(
            AuthActions.openRegisterLoginDialog({
              checkProfile: true,
              onSuccess: () => SelectedItemActions.checkPaymentRequired(),
            }),
          )
        } else {
          if (selectedProfile) {
            actions.push(
              SelectedProfileActions.syncProfile({
                showLoader: true,
                showSnackBarOnFail: true,
                checkProfile: true,
                onSuccess: () => SelectedItemActions.checkPaymentRequired(),
              }),
            )
          } else if (authProfile.type === ProfileTypeEnum.Volunteer) {
            let onSelectGuestActions: Action[] = [SelectedItemActions.placeItemActivity()]
            if (item && [ItemTypeEnum.Raffle, ItemTypeEnum.Purchase].includes(item.item_type)) {
              onSelectGuestActions.push(ProfileItemActivitiesActions.loadProfileItemActivityVolunteer())
            }
            actions.push(ProfileActions.openGuestSelect({ onSelectGuestActions }))
          }
        }

        return actions
      }),
    )
  })

  trackAddToCartEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.placeItemActivity),
      withLatestFrom(this.selectedItemFacade.selectedItem$),
      map(([, selectedItem]) =>
        GoogleAnalyticsActions.trackAddToCartEvent({
          item: this.createGoogleAnalyticsItem(selectedItem),
        }),
      ),
    ),
  )

  checkPaymentRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.checkPaymentRequired),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.authFacade.isGuest$,
        this.authFacade.isVolunteer$,
        this.selectedProfileFacade.selectedProfileCreditCardRequired$,
      ),
      map(([, item, isGuest, isVolunteer, creditCardRequired]) => {
        const paymentRequired = this.itemRequiresPayment(item)
        if (isGuest) {
          if (paymentRequired) return SelectedItemActions.openPaymentDialog()
          if (creditCardRequired)
            return StripeActions.createPreAuthPaymentIntent({
              onSuccess: () => SelectedItemActions.openPreAuthDialog(),
            })
        }

        if (isVolunteer) {
          if (item?.item_type === ItemTypeEnum.Raffle) return SelectedItemActions.checkRemainingRaffleUnits()
          if (item?.item_type === ItemTypeEnum.Purchase) return SelectedItemActions.checkRemainingPurchaseUnits()
          if (this.isLiveBiddingItem(item)) return SelectedItemActions.openAddLiveBidPaymentDialog()
          if (paymentRequired) return SelectedItemActions.openPaymentDialog()
          if (creditCardRequired) return SelectedItemActions.openPreAuthDialog()
          return SelectedItemActions.openConfirmationDialogVolunteer()
        }

        return SelectedItemActions.confirmItemActivity()
      }),
    ),
  )

  openPaymentDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SelectedItemActions.openPaymentDialog),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.selectedItemFacade.currentAmount$,
        this.gbRaffleItemHelper.selectedRafflePackage$,
      ),
      filter(([, selectedItem]) => !!selectedItem),
      exhaustMap(([, item, amount, rafflePackage]) => {
        const pAmount = this.calculateSingleItemPaymentAmount(amount, item!, rafflePackage)

        const paymentDialogConfig: GbPaymentDialogConfig = {
          item,
          paymentType: PaymentTypeEnum.Item,
          paymentAmount: pAmount,
          quantity: item?.item_type !== ItemTypeEnum.Donation ? amount : undefined,
        }

        return this.gbFrontDialogService.getDialogObs(GbPaymentDialogComponent, {
          data: paymentDialogConfig,
          disableClose: true,
        })
      }),
      switchMap((result) => {
        const actions: Action[] = []
        // on cancel
        if (!result || result === 'cancel') {
          actions.push(SelectedItemActions.cancelPayment())
        }
        return actions
      }),
    )
  })

  cancelPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.cancelPayment),
      switchMap(() => [PaymentActions.cancelPayment(), SelectedProfileActions.clearSelectedProfileFieldsVolunteer()]),
    ),
  )

  trackRemoveFromCartEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.cancelPayment),
      withLatestFrom(this.selectedItemFacade.selectedItem$),
      map(([, selectedItem]) =>
        GoogleAnalyticsActions.trackRemoveFromCartEvent({
          item: this.createGoogleAnalyticsItem(selectedItem),
        }),
      ),
    ),
  )

  openPreAuthDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.openPreAuthDialog),
      exhaustMap((_) => this.gbFrontDialogService.getDialogObs(GbPreAuthDialogComponent, { disableClose: true })),
      withLatestFrom(this.authFacade.isVolunteer$),
      switchMap(([result, isVolunteer]) => {
        const actions: Action[] = []
        // on cancel or manual card add
        if (!result || result === PreAuthResultEnum.ManualCard) {
          actions.push(StripeActions.cancelPaymentIntent())
        }

        if (result) {
          isVolunteer
            ? actions.push(SelectedItemActions.openConfirmationDialogVolunteer())
            : actions.push(SelectedItemActions.confirmItemActivity())
        }

        return actions
      }),
    ),
  )

  closePreAuthDialogOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.preAuthSuccess),
        map((action) => {
          this.gbFrontDialogService.closeDialog(GbPreAuthDialogComponent, action.result)
          if (action.result === PreAuthResultEnum.DigitalWallet)
            this.notificationService.success('PRE_AUTH.DIGITAL_WALLET_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  openConfirmationDialogVolunteer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.openConfirmationDialogVolunteer),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.selectedItemFacade.currentAmount$,
        this.selectedProfileFacade.selectedProfile$,
      ),
      exhaustMap(([action, item, amount, selectedProfile]) =>
        this.gbFrontDialogService.getDialogObs(GbSelectGuestConfirmationDialogComponent, {
          width: '400px',
          data: {
            item: item,
            paymentAmount: this.calculateSingleItemPaymentAmount(amount, item),
            profile: selectedProfile,
          },
        }),
      ),
      filter((result) => !!result),
      // on confirm
      map(() => SelectedItemActions.confirmItemActivity()),
    ),
  )

  confirmItemActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.confirmItemActivity),
      withLatestFrom(this.selectedItemFacade.selectedItem$, this.authFacade.isVolunteer$),
      filter(([, item]) => !!item),
      map(([, item, isVolunteer]) => {
        switch (item!.item_type) {
          case ItemTypeEnum.Bidding:
            if (this.isLiveBiddingItem(item)) return SelectedItemActions.placeLiveBid({ payNow: true })
            return isVolunteer ? SelectedItemActions.placeBid() : SelectedItemActions.openConfirmBidDialog()

          case ItemTypeEnum.Donation:
            return SelectedItemActions.placeDonation()

          case ItemTypeEnum.Purchase:
            return SelectedItemActions.placePurchase()

          case ItemTypeEnum.Raffle:
            return SelectedItemActions.placeRafflePurchase()
        }
      }),
    ),
  )

  dispatchConfirmItemActivitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SelectedItemActions.placeBidSuccess,
        SelectedItemActions.placeDonationSuccess,
        SelectedItemActions.placePurchaseSuccess,
        SelectedItemActions.placeRafflePurchaseSuccess,
        SelectedItemActions.placeLiveBidSuccess,
      ),
      withLatestFrom(this.authFacade.isVolunteer$, this.auctionFacade.selectedPaymentMethod$),
      mergeMap(([action, isVolunteer, selectedPaymentMethod]) => {
        const actions: Action[] = []

        if (
          !action.requiresPayment ||
          (isVolunteer && selectedPaymentMethod?.payment_process !== PaymentProcessEnum.Stripe)
        ) {
          // any non-stripe payment method does not require any extra step
          actions.push(
            SelectedItemActions.confirmItemActivitySuccess({
              payment: action.payment,
            }),
          )
        }
        return actions
      }),
    ),
  )

  progressPaymentStepper$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SelectedItemActions.placeDonationSuccess,
          SelectedItemActions.placePurchaseSuccess,
          SelectedItemActions.placeRafflePurchaseSuccess,
          SelectedItemActions.placeLiveBidSuccess,
        ),
        map((action) => {
          if (action.requiresPayment) {
            this.gbPaymentStepperViewController.progressStepper()
            this.gbStripeService.payment = action.payment
          }
        }),
      ),
    { dispatch: false },
  )

  trackBeginCheckoutEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SelectedItemActions.placeDonationSuccess,
        SelectedItemActions.placePurchaseSuccess,
        SelectedItemActions.placeRafflePurchaseSuccess,
        SelectedItemActions.placeLiveBidSuccess,
      ),
      withLatestFrom(this.selectedItemFacade.selectedItem$),
      filter(([action]) => !!action.requiresPayment),
      map(([, selectedItem]) =>
        GoogleAnalyticsActions.trackBeginCheckoutEvent({
          item: this.createGoogleAnalyticsItem(selectedItem),
        }),
      ),
    ),
  )

  resyncItemOnActionFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SelectedItemActions.placeBidFailure,
        SelectedItemActions.placeDonationFailure,
        SelectedItemActions.placePurchaseFailure,
        SelectedItemActions.placeRafflePurchaseFailure,
      ),
      withLatestFrom(this.selectedItemFacade.selectedItemId$),
      map(([_, selectedItemId]) => SelectedItemActions.syncSelectedItem({ uuid: selectedItemId })),
    ),
  )

  closePaymentDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SelectedItemActions.confirmItemActivitySuccess,
          SelectedItemActions.placeDonationFailure,
          SelectedItemActions.placeRafflePurchaseFailure,
          SelectedItemActions.placePurchaseFailure,
        ),
        map((action) => {
          this.gbFrontDialogService.closeDialog(GbPaymentDialogComponent, !('error' in action))
        }),
      ),
    { dispatch: false },
  )

  confirmItemActivitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.confirmItemActivitySuccess),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.selectedProfileFacade.selectedProfile$,
        this.auctionFacade.giftAidEnabled$,
      ),
      switchMap(([, item, selectedProfile, giftAidEnabled]) => {
        const actions: Action[] = []
        if (item) {
          switch (item.item_type) {
            case ItemTypeEnum.Bidding:
              actions.push(SelectedItemActions.finaliseBidSuccess())
              break

            case ItemTypeEnum.Donation:
              if (giftAidEnabled && !selectedProfile?.gift_aid_consented) {
                actions.push(GiftAidActions.loadGiftAid())
                actions.push(GiftAidActions.showGiftAidDialog())
              } else {
                actions.push(SelectedItemActions.finaliseDonationSuccess())
              }
              break
            case ItemTypeEnum.Purchase:
              actions.push(SelectedItemActions.finalisePurchaseSuccess())
              break
            case ItemTypeEnum.Raffle:
              actions.push(SelectedItemActions.finaliseRafflePurchaseSuccess())
              break
          }
        }
        return actions
      }),
    ),
  )

  trackPurchaseEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectedItemActions.confirmItemActivitySuccess),
      withLatestFrom(
        this.selectedItemFacade.selectedItem$,
        this.selectedItemFacade.currentAmount$,
        this.auctionFacade.auctionCurrency$,
        this.gbRaffleItemHelper.selectedRafflePackage$,
      ),
      map(([action, selectedItem, amount, currency, rafflePackage]) =>
        GoogleAnalyticsActions.trackPurchaseEvent({
          item: this.createGoogleAnalyticsItem(selectedItem),
          currency,
          value: this.calculateSingleItemPaymentAmount(amount, selectedItem, rafflePackage),
          tax: action.payment?.tax_amount,
          transaction_id: action.payment?.uuid,
        }),
      ),
    ),
  )

  openItemTnCDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectedItemActions.openItemTnCDialog),
        exhaustMap((_) =>
          this.gbFrontDialogService.getDialogObs(GbItemTermsAndConditionsDialogComponent, {
            minWidth: '400px',
            maxWidth: '600px',
            maxHeight: '90vh',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  itemActivitySuccessClear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SelectedItemActions.finaliseBidSuccess,
        SelectedItemActions.finaliseDonationSuccess,
        SelectedItemActions.finalisePurchaseSuccess,
        SelectedItemActions.finaliseRafflePurchaseSuccess,
      ),
      switchMap(() => [
        PaymentActions.clearSelectedPaymentMethod(),
        SelectedItemActions.clearAmount(),
        SelectedProfileActions.clearSelectedProfileFieldsVolunteer(),
      ]),
    ),
  )

  constructor(
    private actions$: Actions,
    private selectedItemApiService: SelectedItemApiService,
    private selectedItemFacade: SelectedItemFacade,
    private selectedProfileFacade: SelectedProfileFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private itemsFacade: ItemFacade,
    private biddingItemHelperService: GbBiddingItemHelperService,
    private gbFrontDialogService: GbFrontDialogService,
    private gbStripeService: GbStripeService,
    private gbPaymentStepperViewController: GbPaymentStepperViewController,
    private gbRaffleItemHelper: GbRaffleItemHelperService,
    private notificationService: GbNotificationService,
  ) {}

  /**
   * Check if the item is pay now
   * @param item
   * It must check the item.item_type as some very rare cases there are items
   * with 2 concrete item on the API like an item.bidding_item = {...} and
   * the same item with item.bidding_item = {...} it is better check against the type too
   */
  itemRequiresPayment(item: ItemEntity | undefined) {
    const paymentRequired: boolean | undefined = !!(
      item?.item_type === ItemTypeEnum.Raffle ||
      (item?.item_type === ItemTypeEnum.Donation && item?.donation_item?.pay_now) ||
      (item?.item_type === ItemTypeEnum.Purchase && item?.purchase_item?.pay_now)
    )

    return paymentRequired
  }

  isLiveBiddingItem(item: ItemEntity | undefined): boolean {
    if (!item || item.item_type !== ItemTypeEnum.Bidding) return false
    return this.biddingItemHelperService.isLiveBiddingItem(item.bidding_item)
  }

  calculateSingleItemPaymentAmount(quantity: number, item?: ItemEntity, ticketPackage?: TicketPackageEntity): number {
    switch (item?.item_type) {
      case ItemTypeEnum.Bidding:
        return quantity
      case ItemTypeEnum.Donation:
        return quantity
      case ItemTypeEnum.Purchase:
        if (item.purchase_item) return quantity * item.purchase_item.unit_price
        break
      case ItemTypeEnum.Raffle:
        if (item.raffle_item) {
          if (ticketPackage) {
            return ticketPackage.price
          } else {
            return quantity * item.raffle_item.ticket_price
          }
        }
        break
    }
    return 0
  }

  private createGoogleAnalyticsItem(item?: SelectedItemState): GoogleAnalyticsItem {
    return {
      item_id: item?.uuid,
      item_name: item?.name,
      item_category: item?.item_type,
      quantity: item?.item_type !== ItemTypeEnum.Donation && item?.amount ? item.amount : undefined,
    }
  }
}
